/**
 * The "not in a workspace" state, at the app shell.
 *
 * Shown when an onboarded user has been confirmed to have no workspace at all
 * (`needsWorkspaceCreation`, set by WorkspaceContext once the server's list
 * comes back empty): typically the only workspace they were in was deleted by
 * its owner. Creating a workspace goes through support, so it says how to get
 * back in. At the shell, not on one page, so phones (redirected to capture),
 * deep links and the dashboard all see it. Settings stays reachable, to sign
 * out or delete the account. A failed load (`error`) is not this state.
 */

import type { ReactNode } from 'react';
import { Box, Button, Container, Heading, HStack, Link, Text, VStack } from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { ROUTES } from '../../constants/routes';

export function NoWorkspaceGate({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const { workspaceId, needsWorkspaceCreation, error } = useWorkspace();
  const location = useLocation();

  const stranded =
    Boolean(user?.onboarded) &&
    needsWorkspaceCreation &&
    !workspaceId &&
    !error &&
    !location.pathname.startsWith(ROUTES.settings);
  if (!stranded) return <>{children}</>;

  return (
    <Container maxW="container.md" py={16}>
      <VStack spacing={6} align="center" textAlign="center">
        <Box>
          <Heading size="lg" color="text.primary" mb={4}>
            You&apos;re not in a workspace
          </Heading>
          <Text color="text.muted" maxW="md">
            If a workspace you were in has been deleted, ask the owner of another workspace to
            invite you, or email <Link href="mailto:hello@bowerlabs.ai">hello@bowerlabs.ai</Link>{' '}
            and we&apos;ll set up a new one for you.
          </Text>
        </Box>
        <HStack spacing={3}>
          <Button as={RouterLink} to={ROUTES.settings} variant="outline">
            Account settings
          </Button>
          <Button variant="ghost" onClick={() => void signOut()}>
            Sign out
          </Button>
        </HStack>
      </VStack>
    </Container>
  );
}
